import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calculator, Check, Clock, Home, Sparkles, Info, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const translations = {
  sv: {
    title: 'Räkna ut ditt pris',
    subtitle: 'Få en prisuppskattning för din städning',
    size: 'Storlek (m²)',
    frequency: 'Hur ofta?',
    addons: 'Tilläggstjänster',
    windowCount: 'Antal fönster',
    rutDeduction: 'RUT-avdrag',
    rutInfo: 'Skattereduktion för hushållsnära tjänster',
    summary: 'Prissammanfattning',
    estimatedTime: 'Beräknad tid',
    hours: 'timmar',
    hourlyRate: 'Timpris',
    basePrice: 'Grundpris',
    addonsTotal: 'Tillägg',
    subtotal: 'Delsumma',
    rutAmount: 'RUT-avdrag',
    total: 'Att betala',
    perSession: 'per tillfälle',
    bookNow: 'Boka nu',
    getQuote: 'Få offert',
    priceNote: '* Priserna är ungefärliga och kan variera beroende på städningens omfattning',
    afterRut: 'efter RUT-avdrag'
  },
  en: {
    title: 'Calculate your price',
    subtitle: 'Get a price estimate for your cleaning',
    size: 'Size (m²)',
    frequency: 'How often?',
    addons: 'Additional services',
    windowCount: 'Number of windows',
    rutDeduction: 'RUT deduction',
    rutInfo: 'Tax reduction for household services',
    summary: 'Price summary',
    estimatedTime: 'Estimated time',
    hours: 'hours',
    hourlyRate: 'Hourly rate',
    basePrice: 'Base price',
    addonsTotal: 'Add-ons',
    subtotal: 'Subtotal',
    rutAmount: 'RUT deduction',
    total: 'To pay',
    perSession: 'per session',
    bookNow: 'Book now',
    getQuote: 'Get quote',
    priceNote: '* Prices are approximate and may vary depending on the scope of cleaning',
    afterRut: 'after RUT deduction'
  }
};

// Default config (fallback if API fails)
const DEFAULT_CONFIG = {
  frequencies: [
    { id: 'weekly', label_sv: 'Varje vecka', label_en: 'Every week', hourly_rate: 518 },
    { id: 'biweekly', label_sv: 'Varannan vecka', label_en: 'Every 2 weeks', hourly_rate: 518 },
    { id: 'monthly', label_sv: 'Var 4:e vecka', label_en: 'Every 4 weeks', hourly_rate: 600 }
  ],
  size_ranges: [
    { min_sqm: 0, max_sqm: 45, weekly: { base_hours: 1.667, hours_per_sqm: 0 }, biweekly: { base_hours: 1.85, hours_per_sqm: 0 }, monthly: { base_hours: 2.0, hours_per_sqm: 0 } },
    { min_sqm: 45, max_sqm: 55, weekly: { base_hours: 1.75, hours_per_sqm: 0.033 }, biweekly: { base_hours: 2.00, hours_per_sqm: 0.033 }, monthly: { base_hours: 2.2, hours_per_sqm: 0.04 } },
    { min_sqm: 55, max_sqm: 85, weekly: { base_hours: 2.085, hours_per_sqm: 0.023 }, biweekly: { base_hours: 2.333, hours_per_sqm: 0.023 }, monthly: { base_hours: 2.6, hours_per_sqm: 0.023 } },
    { min_sqm: 85, max_sqm: 100, weekly: { base_hours: 2.784, hours_per_sqm: 0.0145 }, biweekly: { base_hours: 3.033, hours_per_sqm: 0.02 }, monthly: { base_hours: 3.3, hours_per_sqm: 0.0245 } },
    { min_sqm: 100, max_sqm: 140, weekly: { base_hours: 3.00, hours_per_sqm: 0.0093 }, biweekly: { base_hours: 3.333, hours_per_sqm: 0.0133 }, monthly: { base_hours: 3.667, hours_per_sqm: 0.0133 } },
    { min_sqm: 140, max_sqm: 200, weekly: { base_hours: 3.373, hours_per_sqm: 0.0133 }, biweekly: { base_hours: 3.865, hours_per_sqm: 0.0133 }, monthly: { base_hours: 4.2, hours_per_sqm: 0.015 } },
    { min_sqm: 200, max_sqm: 250, weekly: { base_hours: 4.17, hours_per_sqm: 0.0133 }, biweekly: { base_hours: 4.666, hours_per_sqm: 0.0133 }, monthly: { base_hours: 5.1, hours_per_sqm: 0.0146 } },
    { min_sqm: 250, max_sqm: 300, weekly: { base_hours: 4.84, hours_per_sqm: 0.0133 }, biweekly: { base_hours: 5.35, hours_per_sqm: 0.0133 }, monthly: { base_hours: 5.833, hours_per_sqm: 0.0133 } },
    { min_sqm: 300, max_sqm: 350, weekly: { base_hours: 5.5, hours_per_sqm: 0.0133 }, biweekly: { base_hours: 6.03, hours_per_sqm: 0.0133 }, monthly: { base_hours: 6.5, hours_per_sqm: 0.0133 } }
  ],
  addons: [
    { id: 'windows', label_sv: 'Fönsterputsning', label_en: 'Window Cleaning', price: 75, unit_sv: 'per fönster', unit_en: 'per window' },
    { id: 'oven', label_sv: 'Ugnsrengöring', label_en: 'Oven Cleaning', price: 350, unit_sv: 'styck', unit_en: 'each' },
    { id: 'fridge', label_sv: 'Kylskåpsrengöring', label_en: 'Fridge Cleaning', price: 250, unit_sv: 'styck', unit_en: 'each' },
    { id: 'balcony', label_sv: 'Balkongstädning', label_en: 'Balcony Cleaning', price: 200, unit_sv: 'styck', unit_en: 'each' }
  ],
  rut_deduction: 0.5
};

const PriceCalculator = () => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);

  const [formData, setFormData] = useState({
    size: 60,
    frequency: 'biweekly',
    addons: [],
    windowCount: 4,
    useRut: true,
  });

  // Fetch config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/site/calculator-config`);
        if (response.ok) {
          const data = await response.json();
          setConfig(data);
        }
      } catch (error) {
        console.error('Error fetching calculator config:', error);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Calculate price based on the pricing config
  const calculation = useMemo(() => {
    const { size, frequency, addons, windowCount, useRut } = formData;
    
    // Find the frequency data
    const freqData = config.frequencies?.find(f => f.id === frequency);
    const hourlyRate = freqData?.hourly_rate || 518;
    
    // Find the appropriate size range
    let rangeData = null;
    for (const range of config.size_ranges || []) {
      if (size >= range.min_sqm && size <= range.max_sqm) {
        rangeData = range;
        break;
      }
      // If size exceeds all ranges, use the last one
      if (size > range.max_sqm) {
        rangeData = range;
      }
    }

    if (!rangeData) {
      rangeData = config.size_ranges?.[0] || DEFAULT_CONFIG.size_ranges[0];
    }

    const pricing = rangeData[frequency] || { base_hours: 2, hours_per_sqm: 0 };
    
    // Calculate hours
    let totalHours = pricing.base_hours;
    if (pricing.hours_per_sqm > 0 && size > rangeData.min_sqm) {
      const extraSqm = Math.min(size - rangeData.min_sqm, rangeData.max_sqm - rangeData.min_sqm);
      totalHours += extraSqm * pricing.hours_per_sqm;
    }

    // Calculate base price
    const basePrice = totalHours * hourlyRate;

    // Calculate addons
    let addonsTotal = 0;
    addons.forEach(addonId => {
      const addon = config.addons?.find(a => a.id === addonId);
      if (addon) {
        if (addonId === 'windows') {
          addonsTotal += addon.price * windowCount;
        } else {
          addonsTotal += addon.price;
        }
      }
    });

    const subtotal = basePrice + addonsTotal;
    const rutDeduction = config.rut_deduction || 0.5;
    const rutAmount = useRut ? subtotal * rutDeduction : 0;
    const total = subtotal - rutAmount;

    return {
      hours: totalHours,
      hourlyRate,
      basePrice: Math.round(basePrice),
      addonsTotal: Math.round(addonsTotal),
      subtotal: Math.round(subtotal),
      rutAmount: Math.round(rutAmount),
      rutPercent: Math.round(rutDeduction * 100),
      total: Math.round(total)
    };
  }, [formData, config]);

  const toggleAddon = (addonId) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addonId)
        ? prev.addons.filter(id => id !== addonId)
        : [...prev.addons, addonId]
    }));
  };

  // Get frequency label based on language
  const getFrequencyLabel = (freq) => {
    return language === 'sv' ? freq.label_sv : freq.label_en;
  };

  // Get addon label based on language
  const getAddonLabel = (addon) => {
    return language === 'sv' ? addon.label_sv : addon.label_en;
  };

  // Get addon unit based on language
  const getAddonUnit = (addon) => {
    return language === 'sv' ? addon.unit_sv : addon.unit_en;
  };

  // Calculate price after RUT for display
  const getAfterRutRate = (hourlyRate) => {
    return Math.round(hourlyRate * (1 - (config.rut_deduction || 0.5)));
  };

  if (configLoading) {
    return (
      <section id="calculator" className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 flex justify-center items-center min-h-[400px]">
          <Loader2 className="animate-spin text-purple-600" size={40} />
        </div>
      </section>
    );
  }

  return (
    <section id="calculator" className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calculator size={16} />
            {language === 'sv' ? 'Prisberäknare' : 'Price Calculator'}
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.title}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Calculator Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            
            {/* Size Slider */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <Label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Home size={20} className="text-purple-600" />
                  {t.size}
                </Label>
                <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-full font-bold text-lg">
                  {formData.size} m²
                </div>
              </div>
              <Slider
                value={[formData.size]}
                onValueChange={([value]) => setFormData(prev => ({ ...prev, size: value }))}
                min={20}
                max={350}
                step={5}
                className="py-4"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>20 m²</span>
                <span>350 m²</span>
              </div>
            </div>

            {/* Frequency Selection */}
            <div className="mb-8">
              <Label className="text-lg font-semibold text-gray-900 mb-4 block flex items-center gap-2">
                <Clock size={20} className="text-purple-600" />
                {t.frequency}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {config.frequencies?.map((freq) => (
                  <button
                    key={freq.id}
                    onClick={() => setFormData(prev => ({ ...prev, frequency: freq.id }))}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.frequency === freq.id
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${
                        formData.frequency === freq.id ? 'text-purple-700' : 'text-gray-700'
                      }`}>
                        {getFrequencyLabel(freq)}
                      </span>
                      {formData.frequency === freq.id && (
                        <Check size={20} className="text-purple-600" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {getAfterRutRate(freq.hourly_rate)} kr/h <span className="text-xs">({t.afterRut})</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Addons */}
            <div className="mb-8">
              <Label className="text-lg font-semibold text-gray-900 mb-4 block flex items-center gap-2">
                <Sparkles size={20} className="text-purple-600" />
                {t.addons}
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {config.addons?.map((addon) => (
                  <label
                    key={addon.id}
                    htmlFor={`addon-${addon.id}`}
                    className={`p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.addons.includes(addon.id)
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`addon-${addon.id}`}
                        checked={formData.addons.includes(addon.id)}
                        onCheckedChange={() => toggleAddon(addon.id)}
                        className="flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm sm:text-base truncate">{getAddonLabel(addon)}</div>
                        <div className="text-xs sm:text-sm text-gray-500">
                          {addon.price} kr/{getAddonUnit(addon)}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Window count */}
              {formData.addons.includes('windows') && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <div className="flex justify-between items-center mb-2">
                    <Label className="font-medium">{t.windowCount}</Label>
                    <span className="font-bold text-purple-600">{formData.windowCount}</span>
                  </div>
                  <Slider
                    value={[formData.windowCount]}
                    onValueChange={([value]) => setFormData(prev => ({ ...prev, windowCount: value }))}
                    min={1}
                    max={20}
                    step={1}
                  />
                </div>
              )}
            </div>

            {/* RUT Deduction */}
            <div className="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{calculation.rutPercent}%</span>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t.rutDeduction} ({calculation.rutPercent}%)</div>
                    <div className="text-sm text-gray-600">{t.rutInfo}</div>
                  </div>
                </div>
                <Switch
                  checked={formData.useRut}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, useRut: checked }))}
                />
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 md:p-8 text-white shadow-xl sticky top-24">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Calculator size={24} />
                {t.summary}
              </h3>

              <div className="space-y-4">
                {/* Estimated time */}
                <div className="flex justify-between items-center pb-4 border-b border-white/20">
                  <span className="text-purple-200">{t.estimatedTime}</span>
                  <span className="font-semibold text-lg">
                    {calculation.hours.toFixed(1)} {t.hours}
                  </span>
                </div>

                {/* Hourly rate */}
                <div className="flex justify-between items-center">
                  <span className="text-purple-200">{t.hourlyRate}</span>
                  <span>{calculation.hourlyRate} kr/h</span>
                </div>

                {/* Base price */}
                <div className="flex justify-between items-center">
                  <span className="text-purple-200">{t.basePrice}</span>
                  <span>{calculation.basePrice} kr</span>
                </div>

                {/* Addons */}
                {calculation.addonsTotal > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-purple-200">{t.addonsTotal}</span>
                    <span>+{calculation.addonsTotal} kr</span>
                  </div>
                )}

                {/* Subtotal */}
                <div className="flex justify-between items-center pt-4 border-t border-white/20">
                  <span className="text-purple-200">{t.subtotal}</span>
                  <span className="font-semibold">{calculation.subtotal} kr</span>
                </div>

                {/* RUT Deduction */}
                {formData.useRut && (
                  <div className="flex justify-between items-center text-orange-300">
                    <span>{t.rutAmount}</span>
                    <span>-{calculation.rutAmount} kr</span>
                  </div>
                )}

                {/* Total */}
                <div className="pt-4 border-t border-white/20">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold">{t.total}</span>
                    <div className="text-right">
                      <span className="text-3xl font-bold">{calculation.total} kr</span>
                      <div className="text-sm text-purple-200">{t.perSession}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="mt-8 space-y-3">
                <a href="/contact" className="block">
                  <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-semibold">
                    {t.bookNow}
                  </Button>
                </a>
                <a href="/contact" className="block">
                  <Button variant="outline" className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-6">
                    {t.getQuote}
                  </Button>
                </a>
              </div>

              {/* Price note */}
              <p className="text-xs text-purple-200 mt-6 flex items-start gap-2">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                {t.priceNote}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PriceCalculator;
